import { useState } from 'react';
import {
  changeMemberRole,
  fetchMe,
  type ClubMemberRole,
} from '../../../api';
import { User } from '../../../types';
import { canHaveOwnershipShare } from '../../../utils/isGeneralUser';
import { getApiErrorMessage } from '../../../utils/apiError';
import { roleLabel } from '../membersUtils';

type OpenSharesModal = (
  list?: User[],
  activatingIds?: number[],
  leaveUserId?: number | null,
  restoreUserId?: number | null,
  restoreAs?: 'pilot' | 'admin' | null,
  roleChange?: { userId: number; toRole: ClubMemberRole } | null,
  ownerIsPilot?: boolean | null
) => void;

export function useMemberRoles({
  members,
  loadMembers,
  openSharesModal,
  showError,
  showSuccess,
  patchUser,
  setBusyId,
}: {
  members: User[];
  loadMembers: () => Promise<User[]>;
  openSharesModal: OpenSharesModal;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  patchUser: (patch: {
    club_role?: string | null;
    is_pilot?: boolean;
    role?: string;
  }) => void;
  setBusyId: (id: number | null) => void;
}) {
  const [transferring, setTransferring] = useState<User | null>(null);

  const handleRoleChange = async (target: User, role: ClubMemberRole) => {
    if (target.role === role) return;
    if (role === 'owner') {
      setTransferring(target);
      return;
    }
    const wasOwner = canHaveOwnershipShare(target.role, target.is_pilot);
    const willOwner = canHaveOwnershipShare(role);
    if (wasOwner !== willOwner) {
      openSharesModal(members, [], null, null, null, {
        userId: target.id,
        toRole: role,
      });
      return;
    }
    setBusyId(target.id);
    try {
      await changeMemberRole(target.id, role);
      showSuccess(`Роль «${target.name}»: ${roleLabel(role)}`);
      await loadMembers();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка смены роли'));
    }
    setBusyId(null);
  };

  const confirmTransferOwnership = async () => {
    if (!transferring) return;
    const target = transferring;
    setBusyId(target.id);
    try {
      await changeMemberRole(target.id, 'owner');
      showSuccess(`Владение передано «${target.name}»`);
      setTransferring(null);
      try {
        const me = await fetchMe();
        patchUser({
          club_role: me.club_role,
          is_pilot: me.is_pilot,
          role: me.role,
        });
      } catch {
        // список участников всё равно обновим
      }
      await loadMembers();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось передать владение'));
    }
    setBusyId(null);
  };

  return {
    transferring,
    setTransferring,
    handleRoleChange,
    confirmTransferOwnership,
  };
}
